<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../Button.svelte';
  import { PayoutService, type DonationPayout } from '../../../lib/api/charityServices';

  export let params: { id: string };

  let payout: DonationPayout | null = null;
  let loading = true;
  let error: string | null = null;

  // Fetch payout details
  async function fetchPayoutDetails() {
    loading = true;
    error = null;

    try {
      const result = await PayoutService.getPayouts({
        search: '',
        page: 1,
        per_page: 100,
      });

      if (result.success && result.payouts) {
        // Find the specific payout by UUID
        const foundPayout = result.payouts.find(p => p.uuid === params.id);
        
        if (foundPayout) {
          payout = foundPayout;
        } else {
          error = 'Payout not found';
        }
      } else {
        error = result.error || 'Failed to load payout details';
      }
    } catch (err) {
      console.error('Error fetching payout details:', err);
      error = 'An error occurred while fetching payout details';
    } finally {
      loading = false;
    }
  }

  function formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  }

  function formatCurrency(amount: number | undefined): string {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  function getStatusClass(status: string): string {
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending') return 'status-pending';
    if (statusLower === 'sent to l2g') return 'status-sent';
    return 'status-default';
  }

  function goBack() {
    window.history.back();
  }

  // Calculate estimated amounts
  $: totalAmount = parseFloat(payout?.amount || '0');
  $: l2gFeePercentage = 3.5;
  $: l2gFeeAmount = totalAmount * (l2gFeePercentage / 100);
  $: estimatedAmount = totalAmount - l2gFeeAmount;
  $: displayStatus = payout?.employer_payment_uuid ? 'Sent to L2G' : 'Pending';

  onMount(() => {
    fetchPayoutDetails();
  });
</script>

<div class="payout-detail-container">
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading payout details...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <div class="error-icon">⚠️</div>
      <p class="error-message">{error}</p>
      <div class="error-actions">
        <Button variant="secondary" on:click={goBack}>
          ← Back to Upcoming Payouts
        </Button>
        <Button variant="primary" on:click={fetchPayoutDetails}>
          Try Again
        </Button>
      </div>
    </div>
  {:else if payout}
    <div class="detail-content">
      <!-- Header -->
      <div class="detail-header">
        <div class="header-left">
          <Button variant="secondary" on:click={goBack}>
            ← Back to Upcoming Payouts
          </Button>
          <h1 class="page-title">Upcoming Payout Details</h1>
        </div>
        <div class="header-right">
          <div class="status-badge {getStatusClass(displayStatus)}">
            {#if displayStatus === 'Sent to L2G'}
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
              </svg>
            {:else}
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
                <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
              </svg>
            {/if}
            <span>{displayStatus}</span>
          </div>
        </div>
      </div>

      <!-- Payout Information Card -->
      <div class="info-card">
        <h2 class="card-title">Payment Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Company</span>
            <span class="info-value">{payout.company || 'N/A'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Total Amount Received</span>
            <span class="info-value amount-large">{formatCurrency(totalAmount)}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Transaction Fee ({l2gFeePercentage}%)</span>
            <span class="info-value fee-amount">-{formatCurrency(l2gFeeAmount)}</span>
          </div>
          <div class="info-item highlight">
            <span class="info-label">Estimated Amount to Receive</span>
            <span class="info-value amount-highlight">{formatCurrency(estimatedAmount)}</span>
          </div>
        </div>
      </div>

      <!-- Donations Card -->
      {#if payout.donation_breakdown?.data && payout.donation_breakdown.data.length > 0}
        <div class="donations-card">
          <h2 class="card-title">
            Included Donations ({payout.donation_breakdown.data.length})
          </h2>
          <div class="donations-table">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Hours</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {#each payout.donation_breakdown.data as donation}
                  <tr>
                    <td>
                      <div class="employee-info">
                        <span class="employee-name">
                          {donation.user?.name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td>{donation.leave_hours || 0} hrs</td>
                    <td class="amount-cell">{formatCurrency(donation.leave_value)}</td>
                    <td>{formatDate(donation.updated_at)}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      {:else if payout.total_donations}
        <div class="donations-card">
          <h2 class="card-title">
            Included Donations
          </h2>
          <div class="info-box">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <div class="info-box-text">
              <p>This payout includes {payout.total_donations} donation{payout.total_donations !== 1 ? 's' : ''}.</p>
            </div>
          </div>
        </div>
      {/if}

      <!-- Info Box -->
      <div class="info-box">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <div class="info-box-text">
          <strong>Upcoming Payout</strong>
          <p>
            {#if displayStatus === 'Sent to L2G'}
              This payment has been received from the employer and is being processed. You will receive the estimated amount once processing is complete.
            {:else}
              This payment is pending receipt from the employer. The estimated amount shown is calculated after the 3.5% transaction fee.
            {/if}
          </p>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .payout-detail-container {
    padding: 0;
    background-color: #f7fafc;
    min-height: calc(100vh - 70px);
  }

  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }

  .loading-spinner {
    border: 3px solid #f3f3f3;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .error-message {
    color: #dc2626;
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }

  .error-actions {
    display: flex;
    gap: 1rem;
  }

  .detail-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .page-title {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .status-badge svg {
    flex-shrink: 0;
  }

  .status-pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .status-sent {
    background-color: #dbeafe;
    color: #1e40af;
  }

  .status-default {
    background-color: #f3f4f6;
    color: #374151;
  }

  .info-card,
  .donations-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .card-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item.highlight {
    background: #f0fdf4;
    padding: 1rem;
    border-radius: 8px;
    border: 2px solid #86efac;
  }

  .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-value {
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
  }

  .amount-large {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
  }

  .fee-amount {
    font-size: 1.125rem;
    font-weight: 600;
    color: #dc2626;
  }

  .amount-highlight {
    font-size: 1.75rem;
    font-weight: 700;
    color: #059669;
  }

  .donations-table {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  thead {
    background-color: #f9fafb;
  }

  th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    border-bottom: 1px solid #e5e7eb;
  }

  td {
    padding: 1rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
    color: #374151;
  }

  tbody tr:hover {
    background-color: #f9fafb;
  }

  .employee-info {
    display: flex;
    flex-direction: column;
  }

  .employee-name {
    font-weight: 500;
    color: #111827;
  }

  .amount-cell {
    font-weight: 600;
    color: #111827;
  }

  .info-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 1rem;
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .info-box svg {
    flex-shrink: 0;
    color: #3b82f6;
    margin-top: 0.125rem;
  }

  .info-box-text {
    flex: 1;
  }

  .info-box-text strong {
    display: block;
    color: #1e40af;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
  }

  .info-box-text p {
    color: #1e40af;
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .detail-header {
      flex-direction: column;
    }

    .header-right {
      width: 100%;
      justify-content: flex-start;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .donations-table {
      overflow-x: scroll;
    }

    table {
      min-width: 600px;
    }
  }
</style>

